import { Box, Checkbox, Chip, FormControlLabel, Grid, Icon, InputAdornment, Slide, Typography, useMediaQuery } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Layout, createSelectField, createTextField } from '@roc/client-portal-shared/components';
import { LoginLogoImg, useBorrowerPortalMediaQueries } from '@roc/feature-app-core';
import { AutocompleteAddressField, DateField, GoogleMap, IconButton, NumberFormat, Paper, PaperBorderType, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useStore } from '../../../../apps/client-portal-public/src/app/hooks';
import SignatureCanvas from 'react-signature-canvas';


import React, { useEffect, useRef, useState } from 'react';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import Button from '@material-ui/core/Button';
import { formatDate, isNil, isNotBlank, statesAbbreviation, statesNames } from '@roc/feature-utils';
import { format, isValid } from 'date-fns';
import { arrowForward } from 'ionicons/icons';
import { ArrowBack, ArrowForward, CheckCircle, Clear, CloseRounded } from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import { ProgressBar } from './components/progressBar';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/swiper.min.css'
import 'swiper/modules/pagination/pagination.min.css'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bodyBackground: {
      background: "none",
    },
    containerWrapper: {
      padding: "0 16px",
      marginTop: 0,
    },
    paper: {
      padding: theme.spacing(2),
    },
    root: {
      width: '100%',
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
    resetContainer: {
      padding: theme.spacing(3),
    },
    sigCanvas: {
      width: "100%",
      height: 200,
      background: "#f7f7f7",
      border: "1px solid #aaa",
      borderRadius: 16,
    }
  })
);



function getSteps() {
  //return ['Personal Information', 'Payment', 'Consent Agreement', 'Signature'];
  return ['Personal Information', 'Consent Agreement', 'Signature'];
}

const VerticalLinearStepper = ({
  active
}: {
  active: number
}) => {
  const classes = useStyles();
  const steps = getSteps();

  return (
    <div className={classes.root}>
      <Stepper activeStep={active} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label} completed={index < active}>
            <StepLabel>
              <span style={{
                fontWeight: index == active ? 'bold' : 'normal'
              }}>{label}</span>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </div>
  );
}



const useAddressStyles = makeStyles((theme: Theme) => ({
  autocompleteContainer: {
    marginBottom: theme.spacing(2),
  },
  mapStyles: {
    position: 'relative',
    width: '100%',
    height: '150px',
  },
  disabledClass: {
    background: '#eee'
  },
  disabledAutoCompleteClass: {
    '& .MuiInputBase-root': {
      background: '#eee'
    }
  }
}));

const stateOptions = statesAbbreviation.map((abbreviation, i) => ({
  value: abbreviation,
  label: statesNames[i],
}));

const addressLine2Tooltip =
  'Address Line 2 can be used for additional information\nsuch as apartment number, suite number, unit number\nor others details that can help in identifying the\nexact location of the address.';

const STREET_NUMBER_REGEX = /^(\d+)\s(.*)$/;

interface PropertyAddressDetailsProps {
  testId?: string;
  store: any;
  disabled?: boolean;
  standaloneLabel?: boolean;
  showMap?: boolean;
  autoFocus?: boolean;
  addMarkForRequiredFields?: boolean;
}

const PropertyAddressDetails = observer(
  ({
    testId = 'address',
    store,
    disabled = false,
    standaloneLabel = true,
    showMap = true,
    autoFocus = false,
    addMarkForRequiredFields = true,
  }: PropertyAddressDetailsProps) => {
    const classes = useAddressStyles();

    const formFields = store.form.fields;

    const handleAddressChange = event => {
      const { value, fullInfo } = event?.target || {};
      const { address_components, geometry } = fullInfo || {};
      if (!isNil(address_components) && !isNil(geometry)) {
        store.saveAddressField(value, address_components, geometry);
      } else {
        const address = event.target.value;
        store.onFieldChange('address', address);
        if (STREET_NUMBER_REGEX.test(address)) {
          const [, streetNumber, streetName] = address.match(
            STREET_NUMBER_REGEX
          );
          store.onFieldChange('streetName', streetName);
          store.onFieldChange('streetNumber', streetNumber);
        } else {
          store.onFieldChange('streetName', address);
          store.onFieldChange('streetNumber', '');
        }
      }
    };

    const stateOptionsFiltered = [...stateOptions];
    stateOptionsFiltered.shift();

    const showGoogleMap =
      showMap &&
      Boolean(formFields?.latitude?.value && formFields?.longitude?.value);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <AutocompleteAddressField
            label="Address Line 1"
            disabled={disabled}
            readOnly={disabled}
            required={true}
            name="address"
            value={formFields.address.value}
            error={
              isNotBlank(formFields.address.error) ||
              isNotBlank(formFields.__isAddressInCorrectFormat__.error)
            }
            errorText={
              formFields.address.error ||
              formFields.__isAddressInCorrectFormat__.error
            }
            onChange={handleAddressChange}
            fullWidth
            testId={`address-${testId}`}
            standaloneLabel={standaloneLabel}
            autoFocus={autoFocus}
            inputClassname={disabled ? classes.disabledAutoCompleteClass : ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {createTextField({
            label: 'Address Line 2',
            fieldName: 'aptNumber',
            testId: `aptNumber-${testId}`,
            store: store,
            disabled,
            tooltip: addressLine2Tooltip,
            standaloneLabel,
            InputProps: {
              style: {
                background: disabled ? '#eee' : 'none'
              }
            }

          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createTextField({
            label: 'City',
            fieldName: 'city',
            testId: `city-${testId}`,
            store: store,
            disabled,
            standaloneLabel,
            InputProps: {
              style: {
                background: disabled ? '#eee' : 'none'
              }
            }
          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createSelectField({
            store: store,
            testId: `state-${testId}`,
            fieldName: 'state',
            label: 'State',
            options: stateOptionsFiltered,
            disabled,
            standaloneLabel,
            className: disabled ? classes.disabledClass : ''
          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createTextField({
            label: 'Zip Code',
            fieldName: 'zipCode',
            testId: `zipCode-${testId}`,
            store: store,
            disabled,
            standaloneLabel,
            InputProps: {
              style: {
                background: disabled ? '#eee' : 'none'
              }
            }
          })}
        </Grid>
        {showGoogleMap && (
          <Grid item xs={12}>
            <GoogleMap
              mapStyles={classes.mapStyles}
              latitude={formFields.latitude.value}
              longitude={formFields.longitude.value}
            />
          </Grid>
        )}
      </Grid>
    );
  }
);


const PropertyAddress = observer(
  ({ store, disabled = false }: { store: any, disabled?: boolean }) => {
    return (
      <>
        <PropertyAddressDetails
          testId={`${store.key}-addressComponent`}
          store={store}
          showMap={false}
          standaloneLabel={false}
          addMarkForRequiredFields={true}
          disabled={disabled}
        />
      </>
    );
  }
);

const Step1_content = observer(({
  setNextDisabled,
}: {
  setNextDisabled?: (val: boolean) => void;
}) => {
  const classes = useStyles();
  const { creditAuthorizationStore } = useStore();
  const formFields = creditAuthorizationStore.form.fields;

  const manageDate = (fieldName, date) => {
    let formattedDate = null;
    if (isValid(date)) {
      formattedDate = format(date, 'MM/dd/yyyy');
    }
    creditAuthorizationStore.onFieldChange(fieldName, formattedDate);
  };

  useEffect(() => {
    setNextDisabled(false)
  }, [])

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Personal Information" />
              <Box mt={2} mb={4}>
                <Typography>
                  The following information is needed to begin the process of determining your identity and finding your credit report.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="firstName"
                required
                variant="outlined"
                label="First Name"
                value={formFields.firstName.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'firstName',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.firstName.error)}
                helperText={formFields.firstName.error}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="lastName"
                required
                variant="outlined"
                label="Last Name"
                value={formFields.lastName.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'lastName',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.lastName.error)}
                helperText={formFields.lastName.error}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateField
                testId="dob"
                required
                inputVariant="outlined"
                format="MM/dd/yyyy"
                label="Date of Birth"
                value={formFields.dob.value}
                onChange={date => manageDate('dob', date)}
                disableFuture={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                customInput={TextField}
                format="###-##-####"
                mask="_"
                required={true}
                variant="outlined"
                label="SSN"
                testId="ssn"
                value={formFields.ssn.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'ssn',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.ssn.error)}
                helperText={formFields.ssn.error}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Primary Address" />
            </Box>
          </Grid>
          <PropertyAddress store={creditAuthorizationStore} />
        </Grid>
      </Grid>
    </Box>
  )
})


const Step2_content = observer(({
  setNextDisabled,
}: {
  setNextDisabled?: (val: boolean) => void;
}) => {
  const classes = useStyles();
  const { creditAuthorizationStore } = useStore();
  const formFields = creditAuthorizationStore.form.fields;
  const [addressSameAsPrimary, setAddressSameAsPrimary] = useState(false);

  useEffect(() => {
    setNextDisabled(false)
  }, [])

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Payment" />
              <Box mt={2}>
                <Alert severity="info">
                  We charge a $100 fee to cover the cost of processing and retrieving your credit report.
                </Alert>
              </Box>
            </Box>
          </Grid>
          <CreditCardForm />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Billing Address" />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={3}>
              <FormControlLabel
                value={addressSameAsPrimary}
                control={<Checkbox color="primary" />}
                label="Billing address is the same as primary address."
                labelPlacement="end"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAddressSameAsPrimary(e.target.checked)
                }}
              />
            </Box>
          </Grid>
          <PropertyAddress store={creditAuthorizationStore} disabled={addressSameAsPrimary} />
        </Grid>
      </Grid>
    </Box>
  )
})

const Step3_content = observer(({
  setNextDisabled,
  isActive,
}: {
  isActive: boolean;
  setNextDisabled?: (val: boolean) => void;
}) => {
  const classes = useStyles();
  const { creditAuthorizationStore } = useStore();
  const formFields = creditAuthorizationStore.form.fields;
  const [agreed, setAgreed] = useState(false);
  const { isLargeScreen } = useBorrowerPortalMediaQueries();

  useEffect(() => {
    if (isActive) {
      setNextDisabled(!agreed)
    } else {
      setNextDisabled(false);
    }
  }, [agreed, isActive])

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Consent Agreement" />
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={1}>
              <Alert severity="info" style={{
                borderRadius: 16
              }}>
                <AlertTitle><strong>Your Credit Score Stays Safe!</strong></AlertTitle>
                You're authorizing a soft credit inquiry, which means it won't affect your credit score like a hard inquiry would. While it may appear on your credit report, it's only for informational purposes.
              </Alert>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid item xs={12}>
            <Box mb={3}>
              <Paper variant="outlined" style={{ maxHeight: isLargeScreen ? 300 : 150, overflowY: "auto", padding: 24, borderColor: '#ccc' }} borderType={PaperBorderType.SOLID_BORDER}>
                <Typography variant="body2">
                  By proceeding, you authorize and permit CCredit Checker ("Lender"), in its own name or under any trade, assumed, or fictitious name (“doing business as” or “DBA”), along with its designees, agents, brokers, participating lenders, vendors, affiliates, and representatives ("Lender Parties"), to:
                  <br />
                  <ol style={{
                    margin: '16px 32px'
                  }}><li>
                      Verify your past and present credit and background history.
                    </li>
                    <li>
                      Order a consumer credit report (hard or soft), a business credit report (if applicable), and/or a background check, including an investigative consumer report (each referred to as a "Consumer Report").
                    </li></ol>
                  You also authorize Lender and the Lender Parties to share your Consumer Report (or any information extracted from it) with Lender’s or any Lender Party’s financing sources, loan providers, brokers, participating lenders, successors, assigns, affiliates, agents, and representatives.
                  <br /><br />
                  Additionally, you authorize Lender and the Lender Parties to obtain updated credit and background verifications and order updated Consumer Reports periodically in accordance with Lender’s internal policies.
                  <br /><br />
                  You understand and acknowledge that neither Lender nor any Lender Party is responsible for the information contained in a Consumer Report, including any negative findings. You release Lender and all Lender Parties, including their employees, officers, and owners, from any liability related to obtaining or sharing such information.
                  <br /><br />
                  You also acknowledge that Consumer Reports and any information extracted from them will only be used in connection with the loan or credit application you are submitting.
                  <br /><br />
                  A photocopy, scan, or facsimile copy of this Authorization to Obtain Consumer and Credit Reports will be treated as the original.
                  <br /><br />
                  Under the federal Fair Credit Reporting Act, if any adverse action is taken based on your Consumer Report, you may request a copy of the report and a summary of your rights.
                </Typography>
              </Paper>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box px={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    color="primary"
                  />
                }
                label="I authorize and permit CCredit Checker to verify my credit history and obtain a credit report as part of this transaction."
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
})

const VerificationChip = ({
  title,
  color,
  icon,
}: {
  title: string,
  color: 'red' | 'orange' | 'green',
  icon?: React.ReactNode,
}) => {

  return (
    <Typography style={{
      color,
      margin: '4px 0px 0px 0px',
      padding: '2px 8px',
      border: '1px solid',
      borderRadius: 24,
      display: 'flex',
      alignItems: 'center',
      width: 'fit-content',
    }} variant='body1'>
      {icon}{title}
    </Typography>
  )
}

const Step4_content = observer(({
  setNextDisabled,
  isActive,
}: {
  setNextDisabled?: (val: boolean) => void;
  isActive: boolean;
}) => {
  const classes = useStyles();
  const { creditAuthorizationStore } = useStore();
  const formFields = creditAuthorizationStore.form.fields;
  const { isSmallScreen } = useBorrowerPortalMediaQueries();

  const [emailVerifyStep, setEmailVerifyStep] = useState<undefined | 'NOT_VERIFIED' | 'VERIFICATION_PENDING' | 'VERIFIED'>('NOT_VERIFIED');
  const [cellPhoneVerifyStep, setCellPhoneVerifyStep] = useState<undefined | 'NOT_VERIFIED' | 'VERIFICATION_PENDING' | 'VERIFIED'>('NOT_VERIFIED');

  const sigCanvas = useRef(null);

  useEffect(() => {
    if (isActive) {
      setNextDisabled(emailVerifyStep != 'VERIFIED' || cellPhoneVerifyStep != 'VERIFIED');
    } else {
      setNextDisabled(false);
    }
  }, [emailVerifyStep, cellPhoneVerifyStep, isActive])

  useEffect(() => {
    if (formFields.emailAddress.value) {
      setEmailVerifyStep('NOT_VERIFIED');
    } else {
      setEmailVerifyStep(undefined);
    }
  }, [formFields.emailAddress.value]);

  useEffect(() => {
    if (formFields.cellPhone.value) {
      setCellPhoneVerifyStep('NOT_VERIFIED');
    } else {
      setCellPhoneVerifyStep(undefined);
    }
  }, [formFields.cellPhone.value]);

  const EmailVerificationStep = (
    <>
      {
        emailVerifyStep == 'NOT_VERIFIED' &&
        <VerificationChip title='Not Verified' color='red' />
      }
      {
        emailVerifyStep == 'VERIFICATION_PENDING' &&
        <VerificationChip title='Verification Pending' color='orange' />
      }
      {
        emailVerifyStep == 'VERIFIED' &&
        <VerificationChip title='Verified' color='green' icon={<CheckCircle style={{
          marginRight: 8
        }} />} />
      }
    </>
  )

  const CellPhoneVerificationStep = (
    <>
      {
        cellPhoneVerifyStep == 'NOT_VERIFIED' &&
        <VerificationChip title='Not Verified' color='red' />
      }
      {
        cellPhoneVerifyStep == 'VERIFICATION_PENDING' &&
        <VerificationChip title='Verification Pending' color='orange' />
      }
      {
        cellPhoneVerifyStep == 'VERIFIED' &&
        <VerificationChip title='Verified' color='green' icon={<CheckCircle style={{
          marginRight: 8
        }} />} />
      }
    </>
  )

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Email" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="emailAddress"
                required
                variant="outlined"
                label="Email Address"
                value={formFields.emailAddress.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'emailAddress',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.emailAddress.error)}
                helperText={formFields.emailAddress.error}
                fullWidth
                disabled={emailVerifyStep == 'VERIFIED'}
              />
              {
                isSmallScreen ? EmailVerificationStep : null
              }
            </Grid>
            {
              isSmallScreen ? null :
                <Grid item xs={12} sm={6} style={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {EmailVerificationStep}
                </Grid>
            }
            {
              emailVerifyStep == 'NOT_VERIFIED' &&
              <>
                <Grid item xs={12} sm={6}>
                  <Button variant='outlined' color='primary' size='small' onClick={() => {
                    setEmailVerifyStep('VERIFICATION_PENDING')
                  }}>
                    Send Verification Code
                  </Button>
                </Grid>
              </>
            }
            {
              emailVerifyStep == 'VERIFICATION_PENDING' &&
              <>
                <Grid item xs={12} sm={6} style={{
                  display: 'flex'
                }}>
                  <div
                    style={{
                      flexGrow: 1,
                    }}>
                    <NumberFormat
                      testId="emailVerificationCode"
                      customInput={TextField}
                      format="#  #  #  #  #  #"
                      mask="_"
                      variant="outlined"
                      label="Email Verification Code"
                      fullWidth
                      required={true}
                    />
                    <Box mt={1} display={'flex'} justifyContent={'space-between'} width="100%" onClick={() => { }}>
                      <Button variant='outlined' color='primary' size='small' onClick={() => {
                        setEmailVerifyStep('VERIFIED')
                      }}>
                        Verify
                      </Button>
                      <Button size="small">Send code again</Button>
                    </Box>
                  </div>
                </Grid>
              </>
            }
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Cell Phone" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                testId="cellPhone"
                customInput={TextField}
                format="(###) ###-####"
                mask="_"
                variant="outlined"
                label="Cell Phone"
                value={formFields.cellPhone.value}
                fullWidth
                required={true}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'cellPhone',
                    e.target.value
                  )
                }
                disabled={cellPhoneVerifyStep == 'VERIFIED'}
              />
              {
                isSmallScreen ? CellPhoneVerificationStep : null
              }
            </Grid>
            {
              isSmallScreen ? null :
                <Grid item xs={12} sm={6} style={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {CellPhoneVerificationStep}
                </Grid>
            }
            {
              cellPhoneVerifyStep == 'NOT_VERIFIED' &&
              <>
                <Grid item xs={12} sm={6}>
                  <Button variant='outlined' color='primary' size='small' onClick={() => {
                    setCellPhoneVerifyStep('VERIFICATION_PENDING')
                  }}>
                    Send Verification Code
                  </Button>
                </Grid>
              </>
            }
            {
              cellPhoneVerifyStep == 'VERIFICATION_PENDING' &&
              <>
                <Grid item xs={12} sm={6} style={{
                  display: 'flex'
                }}>
                  <div
                    style={{
                      flexGrow: 1,
                    }}>
                    <NumberFormat
                      testId="cellPhoneVerificationCode"
                      customInput={TextField}
                      format="#  #  #  #  #  #"
                      mask="_"
                      variant="outlined"
                      label="Cell Phone Verification Code"
                      fullWidth
                      required={true}
                    />
                    <Box mt={1} display={'flex'} justifyContent={'space-between'} width="100%" onClick={() => { }}>
                      <Button variant='outlined' color='primary' size='small' onClick={() => {
                        setCellPhoneVerifyStep('VERIFIED')
                      }}>
                        Verify
                      </Button>
                      <Button size="small">Send code again</Button>
                    </Box>
                  </div>
                </Grid>
              </>
            }
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Signature" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SignatureCanvas penColor='green'
                canvasProps={{ className: classes.sigCanvas }}
                ref={sigCanvas} />
              <Box display={'flex'} justifyContent={'space-between'} width="100%">
                <Button size="small" onClick={() => {
                  const dataURL = sigCanvas.current.toDataURL("image/png");
                  console.log(dataURL);
                  sigCanvas.current.clear();
                }}>Clear</Button>
                <Box>
                  {formatDate(new Date(), 'MM/dd/yyyy')}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
})

const TitleComp = ({
  title
}: {
  title: string;
}) => {
  const { isSmallScreen } = useBorrowerPortalMediaQueries();
  return (
    <>
      <Typography variant={isSmallScreen ? 'h6' : 'h4'}>{title}</Typography>
      <Box width={"100%"} height={'1px'} bgcolor={"#ddd"} mt={1}></Box>
    </>
  )
}

const CreditCardForm = () => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {/* Name on Card */}
        <TextField
          testId='cc_name'
          label="Name on Card"
          variant="outlined"
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12}>
        {/* Credit Card Number */}
        {/* <TextField
          testId='cc_number'
          label="Credit Card Number"
          variant="outlined"
          fullWidth
          placeholder="XXXX XXXX XXXX XXXX"
        /> */}
        <NumberFormat
          testId='cc_number'
          customInput={TextField}
          format="####  ####  ####  ####"
          placeholder="XXXX  XXXX  XXXX  XXXX"
          mask="_"
          required={true}
          variant="outlined"
          label="Credit / Debit Card Number"
          value={undefined}
          onChange={e => { }
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        {/* <TextField
          testId='cc_exp_date'
          label="Exp Date"
          variant="outlined"
          fullWidth
          placeholder="MM/YY"
        /> */}
        <DateField
          testId="cc_exp_date"
          required
          inputVariant="outlined"
          format="MM/yyyy"
          label="Exp Date"
          value={null}
          onChange={date => { }}
          disableFuture={true}
          fullWidth
          placeholder="MM/YY"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          testId='cc_cvv'
          label="Security Code"
          variant="outlined"
          fullWidth
          placeholder="CVV"
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          testId='cc_zip'
          label="ZIP Code"
          variant="outlined"
          fullWidth
          placeholder="XXXXX"
          required
        />
      </Grid>
    </Grid>
  );
};

export const CAF_WITH_EMAIL_PHONE_VERIFICATION = observer(() => {
  const classes = useStyles();
  const { creditAuthorizationStore } = useStore();
  const formFields = creditAuthorizationStore.form.fields;
  const [active, setActive] = useState(0);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const { isSmallScreen, isLargeScreen } = useBorrowerPortalMediaQueries();
  const steps = getSteps();
  const swiperRef = useRef<any>(null);

  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    document.body.classList.add(classes.bodyBackground);

    return () => {
      document.body.classList.remove(classes.bodyBackground);
    };
  }, [classes]);

  useEffect(() => {
    setPercentCompleted(active * 25);
  }, [active]);

  return (
    <Layout maxWidth="lg" bodyContentBoxClass={classes.containerWrapper}>
      <Box mt={isLargeScreen ? 2 : 0}>
        <LoginLogoImg imgStyle={{
          maxWidth: 300
        }} />
      </Box>
      {
        completed ? null :
          <Box mb={4} textAlign={'left'}>
            <Typography variant={isSmallScreen ? 'h5' : 'h3'}>
              Authorization to obtain Consumer and Credit Reports and release
            </Typography>
            <Box>
              <ProgressBar value={percentCompleted} />
            </Box>
          </Box>
      }
      {
        completed ?
          <Grid container spacing={2}>
            <Box mt={3} width={'100%'}>
              <Box textAlign={'center'} margin={'auto'} maxWidth={1000}>
                <Box pb={2} fontSize={32} display={'flex'} alignItems={'center'} justifyContent={'center'} flexDirection={isSmallScreen ? 'column' : 'row'}>
                  <CheckCircle style={{
                    color: 'green',
                    fontSize: isSmallScreen ? 60 : 40,
                    marginRight: 8,
                  }} />
                  <div>
                    Your authorization has been successfully submitted!
                  </div>
                </Box>
                <Box pb={2} fontSize={20}>
                  A confirmation email with the signed form has been sent to you. Thank you!
                </Box>
              </Box>
            </Box>
          </Grid>
          :

          <Grid container spacing={2}>
            <Grid item xs zeroMinWidth>
              <Swiper
                spaceBetween={50}
                slidesPerView={1}
                onSlideChange={(swiper) => {
                  setActive(swiper.realIndex);
                }}
                onSwiper={(swiper) => {
                  (swiperRef.current = swiper)
                }}
                allowTouchMove={false} // Prevents user swiping if not needed
              >
                {steps.map((StepContent, index) => (
                  <SwiperSlide key={index}>
                    {
                      active == 0 && index === 0 && <Step1_content setNextDisabled={setNextDisabled} />
                    }
                    {/* {
                      active == 1 && index === 1 && <Step2_content setNextDisabled={setNextDisabled} />
                    } */}
                    {
                      active == 1 && index === 1 && <Step3_content setNextDisabled={setNextDisabled} isActive={active === index} />
                    }
                    {
                      active == 2 && index === 2 && <Step4_content setNextDisabled={setNextDisabled} isActive={active === index} />
                    }
                  </SwiperSlide>
                ))}
              </Swiper>
              <Grid item xs={12} sm={12}>
                <Box mt={4} display={'flex'} justifyContent={active == 0 ? 'flex-end' : 'space-between'}>
                  {
                    active != 0 &&
                    <Button variant='outlined' color='default' startIcon={<ArrowBack />} size='large' onClick={() => {
                      swiperRef.current?.slidePrev();
                    }}>
                      Back
                    </Button>
                  }
                  <Button variant='contained' color='primary' endIcon={active == steps.length - 1 ? null : <ArrowForward />} size='large' onClick={() => {
                    if (active == steps.length - 1) {
                      setCompleted(true);
                      setPercentCompleted(100);
                    } else {
                      swiperRef.current?.slideNext();
                    }
                  }} disabled={nextDisabled}>
                    {active == steps.length - 1 ? 'Submit' : 'Next'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            {
              isLargeScreen &&
              <Grid item style={{ height: '100%', width: '1px', background: '#ddd', margin: '0 24px', padding: 0 }}></Grid>
            }
            {
              isLargeScreen &&
              <Grid item style={{ width: 280 }}>
                <VerticalLinearStepper active={active} />
              </Grid>
            }
          </Grid>
      }
    </Layout>
  );
});
